import {
  WebTracerProvider,
  Span,
  BatchSpanProcessor,
  SpanExporter,
  SimpleSpanProcessor,
} from "@opentelemetry/sdk-trace-web";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { Resource } from "@opentelemetry/resources";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import {
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  AlwaysOnSampler,
  AlwaysOffSampler,
} from "@opentelemetry/sdk-trace-web";
import { StructuredLogSpanExporter } from "./structuredSpanExporter";
import { config, IS_LOCAL_DEVELOPMENT, IS_CLIENT } from "lib/config";
import { reportError } from "lib/errors/errors";
import packageInfo from "../../package.json";

const traceConfig = {
  serviceName: packageInfo.name,
  serviceVersion: packageInfo.version,
};

// This is a variable that is set by the OtelProvider.tsx component
// We are using the provider because it has access to the active company id
let companyId = "";

export const setCurrentCompanyId = (activeCompanyId: string) => {
  companyId = activeCompanyId;
};

export const resetTraceCompanyId = () => {
  companyId = "";
};

const addAttributes = (span: Span) => {
  span.setAttribute("page.path", window.location.pathname);
  span.setAttribute("page.params", window.location.search);
  span.setAttribute("page.origin", window.location.origin);
  span.setAttribute("company.id", companyId);
};

class CustomBatchSpanProcessor extends BatchSpanProcessor {
  onStart(span: Span) {
    addAttributes(span);
  }
}

class CustomSimpleSpanProcessor extends SimpleSpanProcessor {
  onStart(span: Span) {
    addAttributes(span);
  }
}

const getExporter = async () => {
  if (IS_LOCAL_DEVELOPMENT) {
    const ZipkinExporter = (await import("@opentelemetry/exporter-zipkin")).ZipkinExporter;
    return new ZipkinExporter();
  }

  return new StructuredLogSpanExporter();
};

const getProcessor = (exporter: SpanExporter) => {
  if (IS_LOCAL_DEVELOPMENT) {
    return new CustomSimpleSpanProcessor(exporter);
  }
  return new CustomBatchSpanProcessor(exporter);
};

const getSampler = () => {
  const samplerType = process.env.TRACE_SAMPLER || "parentbased_traceidratio";

  // Parse and validate the trace ID ratio with proper error handling
  const rawTraceIdRatio = process.env.TRACE_ID_RATIO || "0.1";
  let traceIdRatio = parseFloat(rawTraceIdRatio);

  // Handle invalid parseFloat results and out-of-range values
  if (isNaN(traceIdRatio) || traceIdRatio < 0 || traceIdRatio > 1) {
    const errorMessage = `Invalid TRACE_ID_RATIO configuration: "${rawTraceIdRatio}". Expected a number between 0.0 and 1.0. Falling back to default: 0.1`;

    // Report to error monitoring for production visibility
    reportError(new Error(errorMessage), {
      configurationError: "TRACE_ID_RATIO",
      providedValue: rawTraceIdRatio,
      fallbackValue: 0.1,
      validRange: "0.0 - 1.0",
    });

    traceIdRatio = 0.1;
  }

  switch (samplerType) {
    case "always_on":
      return new AlwaysOnSampler();
    case "always_off":
      return new AlwaysOffSampler();
    case "traceidratio":
      return new TraceIdRatioBasedSampler(traceIdRatio);
    case "parentbased_traceidratio":
    default:
      return new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(traceIdRatio),
      });
  }
};

export const initOtelTracing = async () => {
  if (IS_CLIENT && config.TRACE_ENABLED) {
    const resourceSettings = new Resource({
      [ATTR_SERVICE_NAME]: traceConfig.serviceName,
      [ATTR_SERVICE_VERSION]: traceConfig.serviceVersion,
    });

    const sampler = getSampler();
    const exporter = (await getExporter()) as SpanExporter;
    const processor = getProcessor(exporter);

    const provider = new WebTracerProvider({
      resource: resourceSettings,
      sampler: sampler,
      spanProcessors: [processor],
    });

    provider.register();

    registerInstrumentations({
      instrumentations: [
        getWebAutoInstrumentations({
          "@opentelemetry/instrumentation-document-load": {
            enabled: true,
          },
          "@opentelemetry/instrumentation-xml-http-request": {
            enabled: true,
          },
          "@opentelemetry/instrumentation-user-interaction": {
            enabled: false,
          },
        }),
      ],
    });
  }
};
